import { useState, useEffect } from 'react';
import { Box, CircularProgress, Grid, TextField, Stack, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import { getBusinessTypeById, createBusinessType, updateBusinessType } from '../../../stores/businessTypeStore';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const BusinessTypeAddEdit = ({ open, id, onClose, onSuccess }) => {
  const [initialValues, setInitialValues] = useState({
    name: '',
    description: ''
  });
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const notify = useNotifier();

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchInitial = async () => {
      setLoading(true);
      try {
        if (id) {
          const data = await getBusinessTypeById(id);
          if (!mounted) return;
          if (data) {
            setInitialValues({
              name: data.name ?? '',
              description: data.description ?? ''
            });
          }
        } else {
          setInitialValues({
            name: '',
            description: ''
          });
        }
      } catch (err) {
        console.error('Failed to fetch business type:', err);
        notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchInitial();

    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.nameRequired', 'Name is required')),
    description: Yup.string()
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: formatUpperCase(values.name),
          description: values.description
        };
        let response;
        if (id) {
          response = await updateBusinessType(id, payload);
          notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
        } else {
          response = await createBusinessType(payload);
          notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
        }
        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }
        formik.resetForm();
        if (onSuccess) onSuccess();
        else if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('businessType.title', 'Business Type')
          : t('general.add', 'Add') + ' ' + t('businessType.title', 'Business Type')
      }
      maxWidth="xs"
      fullHeight={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', maxWidth: 920 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="medium"
                    id="name"
                    name="name"
                    label={t('general.name', 'Name') + ' *'}
                    value={formik.values.name}
                    onChange={(e) => {
                      formik.setFieldValue('name', formatUpperCase(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && !!formik.errors.name}
                    helperText={formik.touched.name && formik.errors.name}
                  />

                  <TextField
                    fullWidth
                    size="medium"
                    id="description"
                    name="description"
                    label={t('general.description', 'Description')}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.description && !!formik.errors.description}
                    helperText={formik.touched.description && formik.errors.description}
                    multiline
                    rows={3}
                  />
                </Stack>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default BusinessTypeAddEdit;
